/** Official image generation through the authenticated site gateway. */
import { apiClient } from '../../core/api/client';
import type { GeneratedImage } from '../../core/image';

interface ImageResponse {
  data?: { b64_json?: unknown; revised_prompt?: unknown }[];
}
interface ImageItem {
  b64_json?: unknown;
  revised_prompt?: unknown;
}

export interface ServerImageRequest {
  prompt: string;
  model: string;
  size?: string;
  signal?: AbortSignal;
  /** Requires the active plan's auto-image capability on the server. */
  automation?: boolean;
}

export async function generateServerImage(request: ServerImageRequest): Promise<GeneratedImage[]> {
  const response = await apiClient.request<ImageResponse | ImageItem[]>('/v1/images/generations', {
    method: 'POST',
    body: {
      model: request.model,
      prompt: request.prompt,
      n: 1,
      ...(request.automation === true ? { automation: true } : {}),
      ...(request.size === undefined ? {} : { size: request.size }),
    },
    ...(request.signal === undefined ? {} : { signal: request.signal }),
  });
  const images: GeneratedImage[] = [];
  // The shared API client unwraps any top-level `data` envelope. Image
  // responses use `data` as the OpenAI image array, so accept both the raw
  // array returned by the client and the original object shape.
  const entries = Array.isArray(response) ? response : response.data ?? [];
  for (const item of entries) {
    if (typeof item.b64_json !== 'string' || item.b64_json === '') continue;
    images.push({
      dataUrl: `data:image/png;base64,${item.b64_json}`,
      mime: 'image/png',
      ...(typeof item.revised_prompt === 'string' && item.revised_prompt.trim() !== ''
        ? { revisedPrompt: item.revised_prompt }
        : {}),
    });
  }
  if (images.length === 0) throw new Error('生图服务没有返回图片，请稍后重试');
  return images;
}
